import React, { useState } from 'react';
import { RANGE_PRESETS, MAX_RANGE_DAYS, initials, isoDay, rangeLabel, registrationRow, rupees, trendDays, validateRange } from '../../utils/dashboardMetrics';

// Small inline icons: they render the same on every system and follow the text colour.
const ICONS = {
  users: 'M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75',
  drop: 'M12 3s6 6.5 6 11a6 6 0 0 1-12 0c0-4.5 6-11 6-11z',
  inbox: 'M22 12h-6l-2 3h-4l-2-3H2M5.45 5.11L2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z',
  flask: 'M10 2v6.5L4.5 18a2 2 0 0 0 1.7 3h11.6a2 2 0 0 0 1.7-3L14 8.5V2M8.5 2h7M7 15h10',
  stamp: 'M12 15V4M8 4h8M6 15h12v4H6z',
  check: 'M22 11.08V12a10 10 0 1 1-5.93-9.14M22 4L12 14.01l-3-3',
  alert: 'M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0zM12 9v4M12 17h.01',
  rupee: 'M6 3h12M6 8h12M6 13l8.5 8M6 13h3a4 4 0 0 0 0-8',
  plus: 'M12 5v14M5 12h14',
  arrow: 'M5 12h14M13 6l6 6-6 6',
  refresh: 'M21 2v6h-6M3 12a9 9 0 0 1 15-6.7L21 8M3 22v-6h6M21 12a9 9 0 0 1-15 6.7L3 16',
};

export const Icon = ({ name, size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
    <path d={ICONS[name] || ICONS.check} />
  </svg>
);

const show = (v, loading) => (loading ? '…' : v === null || v === undefined ? '—' : v);

/** A card that opens its screen when the user may see it; plain text otherwise. */
const Open = ({ allowed, onOpen, className, children, label }) => (
  allowed
    ? <button type="button" className={`${className} is-link`} onClick={onOpen} aria-label={label}>{children}</button>
    : <div className={className} role="group" aria-label={label}>{children}</div>
);

export const KpiCard = ({ kpi, loading, allowed, onOpen }) => (
  <Open allowed={allowed} onOpen={() => onOpen(kpi.route, kpi.routeParams)} className={`ld-kpi tone-${kpi.tone}`} label={`${kpi.label}: ${kpi.value ?? 'not available'}`}>
    <span className="ld-kpi-icon"><Icon name={kpi.icon} size={20} /></span>
    <span className="ld-kpi-body">
      <span className="ld-kpi-label">{kpi.label}</span>
      <span className="ld-kpi-value">{show(kpi.value, loading)}</span>
      <span className="ld-kpi-hint">{kpi.hint}</span>
      {kpi.progress !== null && kpi.progress !== undefined && (
        <span className="ld-bar" title={`${kpi.progress}% ${kpi.progressLabel}`}><span style={{ width: `${kpi.progress}%` }} /></span>
      )}
    </span>
  </Open>
);

export const PipelineStrip = ({ stages, loading, can, onOpen }) => (
  <ol className="ld-pipeline">
    {stages.map((s, i) => (
      <li key={s.key} className="ld-stage-item">
        <Open allowed={can(s.screen)} onOpen={() => onOpen(s.route)} className="ld-stage" label={s.label}>
          <span className="ld-stage-top"><span className="ld-stage-num">{i + 1}</span><span className="ld-stage-name">{s.label}</span></span>
          <span className="ld-stage-sub">{s.sub}</span>
          <span className="ld-stage-figures">
            {s.waiting !== null && (
              <span className="ld-figure warn"><b>{show(s.waiting, loading)}</b><small>{s.waitingLabel}</small></span>
            )}
            <span className="ld-figure ok"><b>{show(s.done, loading)}</b><small>{s.doneLabel}</small></span>
          </span>
          {s.percent !== null && <span className="ld-bar" title={`${s.percent}% done`}><span style={{ width: `${s.percent}%` }} /></span>}
        </Open>
        {i < stages.length - 1 && <span className="ld-stage-arrow" aria-hidden="true"><Icon name="arrow" size={16} /></span>}
      </li>
    ))}
  </ol>
);

export const AttentionPanel = ({ items, loading, can, onOpen, anyUnavailable }) => (
  <section className="ld-panel" aria-label="Needs attention">
    <header className="ld-panel-head">
      <h2>Needs attention</h2>
      <span className={`ld-pill ${items.length ? 'warn' : 'ok'}`}>{loading ? '…' : items.length ? `${items.length} open` : 'All clear'}</span>
    </header>
    {!loading && items.length === 0 && (
      <p className="ld-empty">{anyUnavailable ? 'Nothing to show from the data that loaded.' : 'No critical results, holds, rejections, overdue samples or unpaid bills.'}</p>
    )}
    <ul className="ld-attention">
      {items.map((it) => (
        <li key={it.key}>
          <Open allowed={can(it.screen)} onOpen={() => onOpen(it.route, it.routeParams)} className={`ld-att sev-${it.severity}`} label={`${it.label}: ${it.count}`}>
            <span className="ld-att-dot" aria-hidden="true" />
            <span className="ld-att-text"><b>{it.label}</b><small>{it.detail}</small></span>
            <span className="ld-att-count">{it.count}</span>
          </Open>
        </li>
      ))}
    </ul>
  </section>
);

export const DepartmentCard = ({ card, loading, allowed, onOpen }) => (
  <section className="ld-panel ld-dept" aria-label={card.title}>
    <header className="ld-panel-head">
      <h2>{card.title}</h2>
      {allowed && <button type="button" className="ld-link" onClick={() => onOpen(card.route)}>Open <Icon name="arrow" size={14} /></button>}
    </header>
    {!card.available && !loading ? <p className="ld-empty">Not available right now.</p> : (
      <>
        <dl className="ld-dept-rows">
          {card.rows.map((r) => (
            <div key={r.label} className={`ld-dept-row ${r.tone ? `tone-${r.tone}` : ''}`}>
              <dt>{r.label}</dt>
              <dd>{show(r.value, loading)}</dd>
            </div>
          ))}
        </dl>
        {card.progress !== null && (
          <div className="ld-dept-progress">
            <span className="ld-bar"><span style={{ width: `${card.progress}%` }} /></span>
            <small>{card.progress}% {card.progressLabel}</small>
          </div>
        )}
      </>
    )}
  </section>
);

export const RecentRegistrations = ({ items, loading, canBill, onOpen }) => {
  const rows = (items || []).map(registrationRow);
  return (
    <section className="ld-panel" aria-label="Recent registrations">
      <header className="ld-panel-head">
        <h2>Recent registrations</h2>
        <button type="button" className="ld-link" onClick={() => onOpen('/registration')}>All registrations <Icon name="arrow" size={14} /></button>
      </header>
      {rows.length === 0 ? (
        <p className="ld-empty">{loading ? 'Loading…' : 'No registrations to show for this centre.'}</p>
      ) : (
        <div className="ld-table-wrap">
          <table className="ld-table">
            <thead><tr><th>Lab no</th><th>Patient</th><th>Age / Gender</th><th className="num">Billed</th><th className="num">Due</th><th>Sample</th>{canBill && <th />}</tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id}>
                  <td><b className="ld-labno">{r.labNo}</b></td>
                  <td>{r.patient}</td>
                  <td>{r.ageGender}</td>
                  <td className="num">{rupees(r.billed)}</td>
                  <td className="num"><span className={`ld-due ${r.due > 0 ? 'owed' : 'clear'}`}>{rupees(r.due)}</span></td>
                  <td><span className={`ld-pill ${r.collected ? 'ok' : 'warn'}`}>{r.collected ? 'Collected' : 'Awaiting'}</span></td>
                  {canBill && <td className="num"><button type="button" className="ld-link" onClick={() => onOpen('/settlement')}>Bill</button></td>}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
};

/** Administrator only: the access summary and the connection health, shown only when the server returned them. */
export const SystemStrip = ({ security, health }) => {
  const cells = [];
  if (security) {
    if (security.totalUsers !== undefined) cells.push(['User accounts', security.totalUsers]);
    if (security.totalRoles !== undefined) cells.push(['Roles', security.totalRoles]);
    if (security.totalWorkspaces !== undefined) cells.push(['Centre–role bindings', security.totalWorkspaces]);
    if (security.totalCentres !== undefined) cells.push(['Centres', security.totalCentres]);
  }
  if (health?.status) cells.push(['System health', health.status]);
  if (cells.length === 0) return null;
  return (
    <section className="ld-system" aria-label="System">
      {cells.map(([label, value]) => (
        <div key={label} className="ld-system-cell"><small>{label}</small><b>{value}</b></div>
      ))}
    </section>
  );
};

/** What people did in a period: everybody for a manager, the user's own numbers for everybody else. */
export const ActivityPanel = ({ view, loading, failed, rangeText }) => (
  <section className="ld-panel" aria-label="Staff activity">
    <header className="ld-panel-head">
      <h2>{view?.everyone ? 'Team activity' : 'My activity'}</h2>
      <span className="ld-range-note">{rangeText}</span>
    </header>

    {failed && !view && <p className="ld-empty">Not available right now.</p>}
    {!failed && !view && <p className="ld-empty">{loading ? 'Loading…' : 'Nothing to show.'}</p>}

    {view && !view.everyone && (
      <>
        <p className="ld-activity-total"><b>{view.total}</b><span>{view.name ? `${view.name} · ` : ''}actions in this period</span></p>
        <dl className="ld-measures">
          {view.measures.map((m) => (
            <div key={m.key} className="ld-measure" title={m.hint}><dt>{m.label}</dt><dd>{m.value}</dd></div>
          ))}
        </dl>
      </>
    )}

    {view && view.everyone && (
      view.count === 0 ? <p className="ld-empty">No activity recorded in this period.</p> : (
        <>
          <p className="ld-activity-total"><b>{view.total}</b><span>actions by {view.count} {view.count === 1 ? 'person' : 'people'}</span></p>
          <ul className="ld-people">
            {view.people.map((p) => (
              <li key={p.id} className="ld-person">
                <span className="ld-avatar" aria-hidden="true">{initials(p.name)}</span>
                <span className="ld-person-main">
                  <span className="ld-person-top"><b>{p.name}</b><span className="ld-person-total">{p.total}</span></span>
                  <span className="ld-bar"><span style={{ width: `${p.percent}%` }} /></span>
                  <span className="ld-chips">
                    {p.parts.map((part) => <span key={part.key} className="ld-chip" title={part.hint}>{part.short} <b>{part.value}</b></span>)}
                  </span>
                </span>
              </li>
            ))}
          </ul>
          {view.hidden > 0 && <p className="ld-empty">and {view.hidden} more</p>}
        </>
      )
    )}
  </section>
);

/** Presets and a calendar. The whole dashboard follows the range chosen here. */
export const RangePicker = ({ range, onChange, today = new Date() }) => {
  const [custom, setCustom] = useState({ from: range.from, to: range.to });
  const [error, setError] = useState(null);
  const customOpen = range.key === 'custom';

  const pickPreset = (key) => {
    setError(null);
    onChange(key, null);
  };
  const apply = () => {
    const problem = validateRange(custom.from, custom.to, today);
    setError(problem);
    if (!problem) onChange('custom', custom);
  };
  const edit = (field, value) => { setCustom((c) => ({ ...c, [field]: value })); setError(null); };

  return (
    <div className="ld-range">
      <div className="ld-seg" role="group" aria-label="Date range">
        {RANGE_PRESETS.map((p) => (
          <button key={p.key} type="button" className={range.key === p.key ? 'is-on' : ''} aria-pressed={range.key === p.key}
            onClick={() => (p.key === 'custom' ? (setError(null), onChange('custom', custom)) : pickPreset(p.key))}>
            {p.label}
          </button>
        ))}
      </div>
      <span className="ld-range-text" aria-live="polite">{rangeLabel(range)}</span>
      {customOpen && (
        <div className="ld-custom">
          <label>From <input type="date" value={custom.from} max={custom.to || undefined} onChange={(e) => edit('from', e.target.value)} aria-label="From date" /></label>
          <label>To <input type="date" value={custom.to} min={custom.from || undefined} max={isoDay(today)} onChange={(e) => edit('to', e.target.value)} aria-label="To date" /></label>
          <button type="button" className="ld-apply" onClick={apply}>Apply</button>
          <span className="ld-hint">up to {MAX_RANGE_DAYS} days</span>
          {error && <span className="ld-error" role="alert">{error}</span>}
        </div>
      )}
    </div>
  );
};

/** Bills, collections and approvals for every day of a longer period. */
export const TrendPanel = ({ period, label }) => {
  const days = trendDays(period);
  if (days.length < 2) return null;
  const showEvery = days.length > 16 ? Math.ceil(days.length / 12) : 1;
  return (
    <section className="ld-panel" aria-label="Daily trend">
      <header className="ld-panel-head">
        <h2>Daily trend</h2>
        <span className="ld-legend">
          <span className="reg">Registered</span><span className="col">Collected</span><span className="apr">Approved</span>
        </span>
      </header>
      <div className="ld-trend" role="img" aria-label={`Daily registrations, collections and approvals, ${label}`}>
        {days.map((d, i) => (
          <div key={d.date} className="ld-trend-day" title={`${d.date}: ${d.registered} registered, ${d.collected} collected, ${d.approved} approved`}>
            <div className="ld-trend-bars">
              <span className="bar reg" style={{ height: `${Math.max(d.registered ? 4 : 0, d.heights.registered)}%` }} />
              <span className="bar col" style={{ height: `${Math.max(d.collected ? 4 : 0, d.heights.collected)}%` }} />
              <span className="bar apr" style={{ height: `${Math.max(d.approved ? 4 : 0, d.heights.approved)}%` }} />
            </div>
            <small>{i % showEvery === 0 ? d.date.slice(8) : ''}</small>
          </div>
        ))}
      </div>
    </section>
  );
};
